<template>
  <v-form ref="form" @submit.prevent="register">
    <v-row dense>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="firstname"
          label="First Name *"
          placeholder="John"
          prepend-inner-icon="mdi-account-outline"
          density="comfortable"
          variant="outlined"
          :rules="[v => !!v || 'First name is required']"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="lastname"
          label="Last Name *"
          placeholder="Doe"
          prepend-inner-icon="mdi-account-outline"
          density="comfortable"
          variant="outlined"
          :rules="[v => !!v || 'Last name is required']"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="name"
          label="Username *"
          placeholder="johndoe"
          hint="English letters and numbers only"
          prepend-inner-icon="mdi-at"
          density="comfortable"
          variant="outlined"
          :rules="[
            v => !!v || 'Username is required',
            v => /^[a-zA-Z0-9]+$/.test(v) || 'Username can only contain English letters and numbers'
          ]"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="email"
          label="Email Address *"
          placeholder="john.doe@example.com"
          type="email"
          prepend-inner-icon="mdi-email-outline"
          density="comfortable"
          variant="outlined"
          :rules="[v => !!v || 'Email is required', v => /.+@.+\..+/.test(v) || 'Email must be valid']"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" sm="8">
        <v-text-field
          v-model="company"
          label="Organization / Company *"
          placeholder="Personal / Company"
          prepend-inner-icon="mdi-office-building-outline"
          density="comfortable"
          variant="outlined"
          :rules="[v => !!v || 'Company is required']"
        />
      </v-col>
      <v-col cols="12" sm="4">
        <v-select
          v-model="selectedCountry"
          :items="countries"
          item-title="name"
          item-value="code"
          label="Country *"
          prepend-inner-icon="mdi-earth"
          return-object
          density="comfortable"
          variant="outlined"
          :rules="[v => !!v || 'Country is required']"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-text-field
          v-model="password"
          label="Password *"
          placeholder="Create a strong password"
          hint="Min 8 characters (uppercase, lowercase, number, special character)"
          persistent-hint
          prepend-inner-icon="mdi-lock-outline"
          density="comfortable"
          variant="outlined"
          class="mb-3"
          :rules="passwordRules"
          :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
          :type="showPassword ? 'text' : 'password'"
          @click:append-inner="showPassword = !showPassword"
        />
      </v-col>
    </v-row>

    <!-- Terms & Consent -->
    <v-card variant="tonal" class="pa-3 mb-4 rounded-xl">
      <v-checkbox
        v-model="gdpr"
        color="primary"
        hide-details
        density="compact"
        :rules="[v => !!v || 'GDPR consent is required']"
      >
        <template #label>
          <span class="text-caption font-weight-medium">
            I agree to the collection and processing of my personal data for the purpose of using <strong>Media Catalog</strong> media catalog.
          </span>
        </template>
      </v-checkbox>
    </v-card>

    <v-btn
      type="submit"
      color="primary"
      size="large"
      block
      class="btn-modern font-weight-bold text-white mt-2"
      :disabled="!gdpr"
      :loading="loading"
      prepend-icon="mdi-account-plus"
    >
      Create Account
    </v-btn>
  </v-form>

  <v-snackbar
    v-model="showSnackbar"
    :timeout="3000"
    color="success"
    location="top"
    rounded="lg"
  >
    <div class="d-flex align-center">
      <v-icon class="mr-2">mdi-check-circle</v-icon>
      Success: An activation email has been sent to your email address. Please check your inbox.
    </div>
  </v-snackbar>

  <v-snackbar
    v-model="showErrorSnackbar"
    :timeout="5000"
    color="error"
    location="top"
    rounded="lg"
  >
    <div class="d-flex align-center">
      <v-icon class="mr-2">mdi-alert-circle</v-icon>
      {{ errorMessage }}
    </div>
    <template #actions>
      <v-btn
        color="white"
        variant="text"
        icon="mdi-close"
        @click="showErrorSnackbar = false"
      />
    </template>
  </v-snackbar>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import Papa from 'papaparse'

export default {
  name: 'RegisterForm',
  setup() {
    const store = useStore()
    const router = useRouter()
    const form = ref(null)
    const name = ref('')
    const email = ref('')
    const password = ref('')
    const firstname = ref('')
    const lastname = ref('')
    const company = ref('')
    const countrycode = ref('')
    const selectedCountry = ref('')
    const countries = ref([])
    const status = 'participant'
    const showSnackbar = ref(false)
    const showErrorSnackbar = ref(false)
    const errorMessage = ref('')
    const okcomm = ref(false)
    const gdpr = ref(false)
    const loading = ref(false)
    const showPassword = ref(false)
    const passwordRules = computed(() => [
      v => !!v || 'Password is required',
      v => v.length >= 8 || 'Password must be at least 8 characters',
      v => /[A-Z]/.test(v) || 'Password must contain at least 1 uppercase letter',
      v => /[a-z]/.test(v) || 'Password must contain at least 1 lowercase letter',
      v => /[0-9]/.test(v) || 'Password must contain at least 1 digit',
      v => /[!@#$%^&*(),.?":{}|<>]/.test(v) || 'Password must contain at least 1 special character',
    ])

    watch([firstname, lastname], ([newFirstname, newLastname]) => {
      if (newFirstname && newLastname) {
        const cleanFirstname = newFirstname[0].replace(/[^a-zA-Z0-9]/g, '')
        const cleanLastname = newLastname.replace(/[^a-zA-Z0-9]/g, '')
        name.value = (cleanFirstname + cleanLastname).toLowerCase()
      }
    })

    watch(selectedCountry, (newValue) => {
      if (newValue && typeof newValue === 'object' && newValue.code) {
        countrycode.value = newValue.code
      } else if (newValue) {
        countrycode.value = newValue
      }
    })

    const loadCSV = async () => {
      try {
        const response = await fetch('countriesdb.csv')
        const csvData = await response.text()
        Papa.parse(csvData, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            countries.value = result.data.map(item => ({
              code: item.code,
              name: item.name,
            }))
          },
          error: (error) => {
            console.error('Error parsing CSV:', error)
          },
        })
      } catch (error) {
        console.error('Error fetching CSV:', error)
      }
    }

    const register = async () => {
      const { valid } = await form.value.validate()
      if (!valid) {
        if (password.value) {
          const errors = []
          if (password.value.length < 8) errors.push('at least 8 characters')
          if (!/[A-Z]/.test(password.value)) errors.push('1 uppercase letter')
          if (!/[a-z]/.test(password.value)) errors.push('1 lowercase letter')
          if (!/[0-9]/.test(password.value)) errors.push('1 digit')
          if (!/[!@#$%^&*(),.?":{}|<>]/.test(password.value)) errors.push('1 special character')

          if (errors.length > 0) {
            errorMessage.value = `Password must contain ${errors.join(', ')}`
            showErrorSnackbar.value = true
          }
        }
        return
      }

      loading.value = true
      try {
        await store.dispatch('auth/register', {
          name: name.value,
          email: email.value,
          password: password.value,
          firstname: firstname.value,
          lastname: lastname.value,
          company: company.value,
          countryname: selectedCountry.value.name,
          countrycode: countrycode.value,
          status: status,
          okcomm: okcomm.value,
        })

        showSnackbar.value = true
        setTimeout(() => {
          router.push('/')
        }, 3000)

      } catch (error) {
        console.error('Registration failed:', error)
        if (error.response?.status === 400 && error.response?.data?.detail === 'Email already exists.') {
          errorMessage.value = 'Email already exists.'
          showErrorSnackbar.value = true
        } else if (error.response?.status === 400 && error.response?.data?.detail === 'Username already exists.') {
          errorMessage.value = 'Username already exists. Please try another username.'
          showErrorSnackbar.value = true
        } else {
          errorMessage.value = 'Registration failed. Please try again.'
          showErrorSnackbar.value = true
        }
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      loadCSV()
    })

    return {
      form,
      name,
      email,
      password,
      register,
      firstname,
      lastname,
      company,
      countrycode,
      selectedCountry,
      countries,
      status,
      showSnackbar,
      showErrorSnackbar,
      errorMessage,
      okcomm,
      gdpr,
      passwordRules,
      loading,
      showPassword,
    }
  },
}
</script>